import React, {useCallback, useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useLocation} from 'react-router-dom';

import {Box, Center, Spinner, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';
import {ensureArray} from '@amblique/king-living/app/utils/utils';
import {FOCUSABLE_SELECTOR} from '@amblique/king-living/app/hooks/use-overlay-initial-focus';

import {useBlurOverlay} from '@amblique/king-living/app/contexts/blur-overlay-context';
import {useHeaderContext} from '@amblique/king-living/app/contexts/header-context';
import {MegaMenuTrigger} from '@amblique/king-living/app/components/mega-menu/desktop/mega-menu-trigger';

const CLOSE_DELAY_MS = 100;

const getTriggerFocusable = (triggerEl) => {
  if (!triggerEl) {
    return null;
  }

  if (triggerEl.matches?.(FOCUSABLE_SELECTOR)) {
    return triggerEl;
  }

  return triggerEl.querySelector(FOCUSABLE_SELECTOR);
};

/**
 * Pre–Header 2.0 desktop nav: L1 category hover row + popover.
 * Used when Contentful has no Primary Items so merch can still author menuItems.
 */
export const MegaMenuDesktopLegacy = ({isLoading = false, menuItems = [], navigationMenu, ...props}) => {
  const location = useLocation();
  const [activeMenuId, setActiveMenuId] = useState(null);
  const [keyboardFocusMenuId, setKeyboardFocusMenuId] = useState(null);
  const closeTimeoutRef = useRef(null);
  const isKeyboardOpenRef = useRef(false);
  const navRef = useRef(null);
  const isMenuOpen = activeMenuId !== null;
  const blurOverlay = useBlurOverlay();
  const wasMenuOpenRef = useRef(false);

  const {setMegaMenuOpen, headerStyle, headerState, isSearchOpen} = useHeaderContext();
  const styles = useMultiStyleConfig('MegaMenu', {
    isLegacyNav: true,
    isMenuOpen,
    variant: headerStyle,
    headerState,
    isSearchOpen,
  });
  const clearCloseTimeout = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const handleMenuOpen = useCallback((menuId) => {
    clearCloseTimeout();
    setKeyboardFocusMenuId(null);

    setActiveMenuId((current) => {
      if (current !== menuId) {
        isKeyboardOpenRef.current = false;
      }

      return menuId;
    });
  }, [clearCloseTimeout]);

  const handleMenuClose = useCallback(() => {
    if (isKeyboardOpenRef.current) {
      return;
    }
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      isKeyboardOpenRef.current = false;
      setKeyboardFocusMenuId(null);
      setActiveMenuId(null);
    }, CLOSE_DELAY_MS);
  }, [clearCloseTimeout]);

  const handleMenuForceClose = useCallback(() => {
    clearCloseTimeout();
    isKeyboardOpenRef.current = false;
    setKeyboardFocusMenuId(null);
    setActiveMenuId(null);
  }, [clearCloseTimeout]);

  useEffect(() => {
    setMegaMenuOpen(isMenuOpen);
  }, [isMenuOpen, setMegaMenuOpen]);

  useEffect(() => {
    handleMenuForceClose();
  }, [location.pathname, location.search, handleMenuForceClose]);

  useEffect(() => {
    if (!isSearchOpen) {
      return;
    }

    clearCloseTimeout();
    isKeyboardOpenRef.current = false;
    setKeyboardFocusMenuId(null);
    setActiveMenuId(null);
  }, [isSearchOpen, clearCloseTimeout]);

  useEffect(() => {
    if (isMenuOpen) {
      blurOverlay.show({
        onClose: handleMenuClose,
        onForceClose: handleMenuForceClose,
        // Sticky header is 1100; keep overlay under it so the in-header hover
        // panel is not covered (Header 2.0 drawer overlay stays at 1590).
        zIndex: 1090,
      });
      wasMenuOpenRef.current = true;
    } else if (wasMenuOpenRef.current) {
      blurOverlay.hide();
      wasMenuOpenRef.current = false;
    }
  }, [isMenuOpen, blurOverlay, handleMenuClose, handleMenuForceClose]);

  const handleMenuToggle = useCallback((menuId) => {
    clearCloseTimeout();

    setActiveMenuId((current) => {
      const willBeOpen = current !== menuId;

      isKeyboardOpenRef.current = willBeOpen;
      setKeyboardFocusMenuId(willBeOpen ? menuId : null);

      return current === menuId ? null : menuId;
    });
  }, [clearCloseTimeout]);

  const handleMenuOpenByKeyboard = useCallback((menuId) => {
    if (!menuId) {
      return;
    }

    clearCloseTimeout();
    isKeyboardOpenRef.current = true;
    setKeyboardFocusMenuId(null);
    setActiveMenuId(menuId);
  }, [clearCloseTimeout]);

  const handleMenuOpenById = useCallback((event) => {
    const menuId = event.currentTarget.dataset.menuId;

    if (menuId) {
      handleMenuOpen(menuId);
    }
  }, [handleMenuOpen]);

  const handleMenuToggleById = useCallback((event) => {
    const menuId = event.currentTarget.dataset.menuId;

    if (menuId) {
      handleMenuToggle(menuId);
    }
  }, [handleMenuToggle]);

  const handleMenuOpenByKeyboardId = useCallback((event) => {
    const menuId = event.currentTarget.dataset.menuId || event.currentTarget.dataset.megaMenuTrigger;

    handleMenuOpenByKeyboard(menuId);
  }, [handleMenuOpenByKeyboard]);

  const getNavTriggers = useCallback(() => {
    return Array.from(navRef.current?.querySelectorAll('[data-mega-menu-trigger]') ?? []);
  }, []);

  const focusNextOutsideNav = useCallback(() => {
    const nav = navRef.current;
    const triggers = getNavTriggers();
    const lastTrigger = triggers[triggers.length - 1];
    const lastFocusable = getTriggerFocusable(lastTrigger);

    if (!lastFocusable) {
      return;
    }

    const allFocusables = Array.from(document.querySelectorAll(FOCUSABLE_SELECTOR));
    const startIdx = allFocusables.indexOf(lastFocusable);

    for (let i = startIdx + 1; i < allFocusables.length; i += 1) {
      const el = allFocusables[i];

      if (nav?.contains(el) || el.closest('[data-mega-menu-panel]')) {
        continue;
      }

      el.focus({preventScroll: true});
      return;
    }
  }, [getNavTriggers]);

  const handleFocusAdjacentTrigger = useCallback((menuId, direction) => {
    const triggers = getNavTriggers();
    const currentIndex = triggers.findIndex((trigger) => trigger.dataset.megaMenuTrigger === menuId);

    if (currentIndex < 0) {
      return;
    }

    const adjacentIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;
    const adjacentTrigger = triggers[adjacentIndex];

    if (!adjacentTrigger) {
      handleMenuForceClose();

      if (direction === 'next') {
        focusNextOutsideNav();
      }

      return;
    }

    const focusable = getTriggerFocusable(adjacentTrigger);

    focusable?.focus({preventScroll: true});
  }, [focusNextOutsideNav, getNavTriggers, handleMenuForceClose]);

  useEffect(() => {
    if (!isMenuOpen || !isKeyboardOpenRef.current) {
      return undefined;
    }

    const handleFocusIn = (event) => {
      if (!isKeyboardOpenRef.current) {
        return;
      }

      const target = event.target;

      if (!(target instanceof Element)) {
        return;
      }

      const inNav = navRef.current?.contains(target);
      const inPanel = Boolean(target.closest('[data-mega-menu-panel]'));

      if (!inNav && !inPanel) {
        handleMenuForceClose();
      }
    };

    document.addEventListener('focusin', handleFocusIn);

    return () => {
      document.removeEventListener('focusin', handleFocusIn);
    };
  }, [handleMenuForceClose, isMenuOpen]);

  if (isLoading) {
    return (
      <Box
        className="mega-menu-desktop-wrapper mega-menu-desktop-wrapper--legacy"
        position="relative"
        {...props}
        {...styles.megaMenuDesktopWrapper}
      >
        <Box as="nav" {...styles.megaMenuDesktopNav}>
          <Center width="100%">
            <Spinner color={styles.megaMenuLoadingSpinner?.color} size="sm" />
          </Center>
        </Box>
      </Box>
    );
  }

  const visibleMenuItems = ensureArray(menuItems).filter(Boolean);

  if (!visibleMenuItems.length) {
    return null;
  }

  return (
    <Box
      className="mega-menu-desktop-wrapper mega-menu-desktop-wrapper--legacy"
      position="relative"
      {...props}
      {...styles.megaMenuDesktopWrapper}
    >
      <Box ref={navRef} as="nav" onMouseLeave={handleMenuClose} {...styles.megaMenuDesktopNav}>
        {visibleMenuItems.map((menuItem) => (
          <MegaMenuTrigger
            key={menuItem.sys?.id}
            focusPanelOnOpen={keyboardFocusMenuId === menuItem.sys?.id}
            isOpen={activeMenuId === menuItem.sys?.id}
            menuId={menuItem.sys?.id}
            menuItem={menuItem}
            navigationMenu={navigationMenu}
            onClose={handleMenuClose}
            onFocusAdjacentTrigger={handleFocusAdjacentTrigger}
            onForceClose={handleMenuForceClose}
            onKeyboardOpen={handleMenuOpenByKeyboardId}
            onOpen={handleMenuOpenById}
            onToggle={handleMenuToggleById}
          />
        ))}
      </Box>
    </Box>
  );
};

MegaMenuDesktopLegacy.displayName = 'MegaMenuDesktopLegacy';
MegaMenuDesktopLegacy.propTypes = {
  isLoading: PropTypes.bool,
  menuItems: PropTypes.array,
  navigationMenu: PropTypes.object,
};
